import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, User, Package, Building, Calendar } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { getBrandLogoUrl } from '../../utils/logoUtils';

interface WarrantyCardPrintViewProps {
  warranty: any;
  settings: any;
}

const BarcodeValue: React.FC<{ value: string }> = ({ value }) => {
  const barcodeRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (barcodeRef.current && value) {
      JsBarcode(barcodeRef.current, value, {
        format: "CODE128",
        width: 1.5,
        height: 30,
        displayValue: false,
        margin: 0
      });
    }
  }, [value]);
  return <svg ref={barcodeRef}></svg>;
};

const WarrantyCardPrintView: React.FC<WarrantyCardPrintViewProps> = ({ warranty, settings }) => {
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  
  useEffect(() => {
    const generateQR = async () => {
      try {
        const portalUrl = `https://theairconsims.netlify.app/CSPortal/Warranty/${warranty?.serialNo}`;
        const url = await QRCode.toDataURL(portalUrl, {
          width: 250,
          margin: 1,
          color: {
            dark: '#003d99',
            light: '#ffffff'
          }
        });
        setQrDataUrl(url);
      } catch (err) {
        console.error('QR generation failed', err);
      }
    };
    if (warranty?.serialNo) {
      generateQR();
    }
  }, [warranty?.serialNo]);

  if (!warranty) return null;

  const company = settings?.company || {};
  
  const formatPurchaseDate = (dateString?: string) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '—';
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'long' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return '—';
    }
  };

  const purchaseDate = warranty.deliveryInvoice?.date 
    ? formatPurchaseDate(warranty.deliveryInvoice.date) 
    : formatPurchaseDate(warranty.deliveryInvoice?.createdAt);

  const warrantyItems = [
    { label: 'Service Warranty', val: warranty.serviceWarrantyYears },
    { label: 'Spare Parts Warranty', val: warranty.sparePartsWarrantyYears },
    { label: 'Compressor Warranty', val: warranty.compressorWarrantyYears },
    { label: 'General Warranty', val: warranty.warrantyYears }
  ].filter(w => w.val && w.val > 0);

  const brandLogoUrl = getBrandLogoUrl(warranty.brand || '', warranty.brandLogo);

  return (
    <div className="warranty-card-container print-invoice-area" style={{
      fontFamily: "'DM Sans', sans-serif",
      width: '4in',
      height: '6in',
      margin: '0',
      padding: '0',
      background: 'white',
      position: 'relative',
      boxSizing: 'border-box',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      lineHeight: '1.1',
      color: '#1e293b'
    }}>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          width: 4in !important;
          height: 6in !important;
        }
        @media print {
          @page {
            size: 4in 6in;
            margin: 0mm !important;
          }
          html, body {
            margin: 0mm !important;
            padding: 0mm !important;
            width: 4in !important;
            height: 6in !important;
            overflow: hidden !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .warranty-card-container {
            width: 4in !important;
            height: 6in !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
        }
      `}</style>
      <div style={{ height: '4px', width: '100%', flexShrink: 0, background: 'linear-gradient(90deg, #003d99, #0066cc, #003d99)' }}></div>
      
      <header style={{ padding: '10px 16px 6px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #003d99', flexShrink: 0 }}>
        <div style={{ width: '60px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', overflow: 'hidden', background: 'white' }}>
          {brandLogoUrl ? (
            <img src={brandLogoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <span style={{ fontSize: '7px', color: '#003d99', textAlign: 'center' }}>BRAND<br/>LOGO</span>
          )}
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '16px', fontWeight: 'bold', color: '#0066cc', margin: 0, letterSpacing: '-0.025em' }}>Warranty Card</h1>
        </div>
        <div style={{ width: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #0066cc, #003d99)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
            <ShieldCheck size={20} color="white" />
          </div>
        </div>
      </header>

      <div style={{ padding: '8px 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', overflow: 'hidden' }}>
        {/* Customer Details */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <User size={10} color="#0066cc" />
            <h2 style={{ fontSize: '8px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0066cc', margin: 0 }}>Customer Details</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1, borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Customer Name</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{warranty.customer.name}</p>
              </div>
              <div style={{ flex: 1, borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Project Name</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{warranty.project.name}</p>
              </div>
            </div>
            <div style={{ borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
              <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Customer Address</p>
              <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{warranty.customer.address || warranty.project.address || warranty.project.projectAddress || '—'}</p>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1, borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Contact No</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{warranty.customer.phone || '—'}</p>
              </div>
              <div style={{ flex: 1, borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Purchase Date</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{purchaseDate}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Product Details */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <Package size={10} color="#0066cc" />
            <h2 style={{ fontSize: '8px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0066cc', margin: 0 }}>Product Details</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
              <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Product Name</p>
              <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{warranty.product.name}</p>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1, borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Brand</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{warranty.brand || '—'}</p>
              </div>
              <div style={{ flex: 1, borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Model</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{warranty.model || '—'}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1, borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Type</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{warranty.type || '—'}</p>
              </div>
              <div style={{ flex: 1, borderBottom: '1px solid #003d99', paddingBottom: '2px' }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Compressor Type</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{warranty.compressorType || '—'}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Provider */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <Building size={10} color="#0066cc" />
            <h2 style={{ fontSize: '8px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0066cc', margin: 0 }}>Warranty Provider</h2>
          </div>
          <div style={{ borderRadius: '8px', padding: '8px', backgroundColor: 'white', border: '1px solid #000080', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div>
              <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Company</p>
              <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{company.name || 'The Aircons Ltd.'}</p>
            </div>
            <div>
              <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Address</p>
              <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{company.address || 'Plot-14, Main Road, Block-C, Banasree, Rampura, Dhaka-1219.'}</p>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Email</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{company.email || 'support@theaircons.com'}</p>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.025em', margin: 0 }}>Phone</p>
                <p style={{ fontSize: '8px', color: '#1e293b', fontWeight: 600, margin: 0 }}>{company.phone || '+880-1749-121607'}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Coverage */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <Calendar size={10} color="#0066cc" />
            <h2 style={{ fontSize: '8px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0066cc', margin: 0 }}>Warranty Coverage</h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {warrantyItems.map((w, idx) => (
              <div key={idx} style={{ flex: 1, minWidth: '60px', borderRadius: '8px', padding: '4px', textAlign: 'center', backgroundColor: 'white', border: '1px solid #000080', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '11px', fontWeight: 'bold', color: '#0066cc', margin: 0 }}>{w.val} {w.val === 1 ? 'Year' : 'Years'}</p>
                <p style={{ fontSize: '7px', fontWeight: 'bold', textTransform: 'uppercase', color: '#003d99', margin: 0, lineHeight: '1.1', wordBreak: 'break-word' }}>{w.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Signature Section */}
        <div style={{ paddingTop: '4px', borderTop: '1px solid #003d99', display: 'flex', flexDirection: 'column', gap: '2px', marginTop: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <p style={{ fontSize: '8px', color: '#003d99', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 'bold', margin: 0 }}>SL: {warranty.serialNo || '—'}</p>
            <div style={{ marginTop: '2px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <BarcodeValue value={warranty.serialNo} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', marginTop: '-4px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: '8px' }}>
              <div style={{ width: '70px', height: '70px', border: '1px solid #003d99', borderRadius: '6px', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2px', overflow: 'hidden' }}>
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR Code" style={{ width: '100%', height: '100%', display: 'block' }} />
                ) : (
                  <div style={{ fontSize: '8px', color: '#ccc' }}>QR</div>
                )}
              </div>
              <p style={{ fontSize: '7px', fontWeight: 'bold', textTransform: 'uppercase', color: '#003d99', margin: 0 }}>Track Warranty</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'end', marginRight: '8px' }}>
              <div style={{ width: '110px', borderBottom: '1px solid #003d99', height: '30px', marginBottom: '2px' }}></div>
              <p style={{ fontSize: '7px', fontWeight: 'bold', textTransform: 'uppercase', color: '#003d99', margin: 0 }}>Authorized Signatory</p>
            </div>
          </div>
        </div>
      </div>

      <footer style={{ padding: '2px 0', textAlign: 'center', borderTop: '1px solid #003d99', backgroundColor: 'white', flexShrink: 0 }}>
        <p style={{ fontSize: '7px', color: '#003d99', fontStyle: 'italic', margin: 0 }}>This warranty certificate is valid subject to company terms & condition.</p>
      </footer>
    </div>
  );
};

export default WarrantyCardPrintView;
